/**
 * Ledger Entry Editor — Date Utilities
 *
 * Pure functions for local-date parsing, formatting, validation, and
 * ISO-local timestamp serialization. All dates are treated as wall-clock
 * local times — never interpreted as UTC unless explicitly requested.
 *
 * Design decisions:
 * - entry_date fields arrive as strings from the backend
 * - UI displays MM/DD/YYYY (US-friendly) with optional HH:mm
 * - Date-only inputs complete to local midnight (new Date(y,m,d))
 * - ISO output omits trailing Z so round-trip preserves wall time
 */

// ===========================================================================
// TYPES
// ===========================================================================

export interface LocalDateParts {
	year: number;
	month: number; // 1–12
	day: number; // 1–31
}

export interface LocalDateTimeParts extends LocalDateParts {
	hours: number; // 0–23
	minutes: number; // 0–59
}

interface ParsedDate {
	valid: boolean;
	parts: LocalDateParts | null;
	isoLocal: string | null; // YYYY-MM-DDTHH:mm:ss or YYYY-MM-DD
	display: string | null; // MM/DD/YYYY
}

export interface ParsedDateTime {
	valid: boolean;
	parts: LocalDateTimeParts | null;
	isoLocal: string | null;
	isoLocalDateOnly: string | null;
	display: string | null; // MM/DD/YYYY
	displayWithTime: string | null; // MM/DD/YYYY HH:mm
	hasExplicitTime: boolean; // true if hours/minutes were explicitly provided
}

// ===========================================================================
// LOW-LEVEL FORMATTING HELPERS
// ===========================================================================

function padNum(n: number, width: number): string {
  return String(n).padStart(width, '0');
}

export function toIsoLocalDateOnly(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, '0')}-${padNum(month, 2)}-${padNum(day, 2)}`;
}

/**
 * Serialize a local date+time to ISO-8601 WITHOUT the trailing 'Z'.
 * Format: YYYY-MM-DDTHH:mm:ss
 *
 * This ensures that when deserialized elsewhere, the wall-clock time is
 * preserved exactly -- no timezone offset correction occurs because there
 * is no timezone designator at all. The receiving system must interpret it
 * as local.
 *
 * For date-only: YYYY-MM-DD
 */
export function toIsoLocal(year: number, month: number, day: number, hours?: number, minutes?: number, seconds?: number): string {
  const base = toIsoLocalDateOnly(year, month, day);
  if (hours == null && minutes == null && seconds == null) return base;
  const h = padNum(hours ?? 0, 2);
  const m = padNum(minutes ?? 0, 2);
  const s = padNum(seconds ?? 0, 2);
  return `${base}T${h}:${m}:${s}`;
}

/**
 * Convert a JS Date to its local ISO-8601 representation (no Z suffix).
 * Preserves the exact wall-clock time of the input Date object.
 */
export function dateToIsoLocal(d: Date): string {
  return toIsoLocal(
    d.getFullYear(),
    d.getMonth() + 1,
    d.getDate(),
    d.getHours(),
    d.getMinutes(),
    d.getSeconds(),
  );
}

/**
 * Format a date as MM/DD/YYYY (US-style friendly display).
 */
export function formatDateDisplay(month: number, day: number, year: number): string {
  return `${padNum(month, 2)}/${padNum(day, 2)}/${String(year).padStart(4, '0')}`;
}

// ===========================================================================
// INTERNAL BUILDERS
// ===========================================================================

function makeInvalidDate(reason = ''): ParsedDate {
  return { valid: false, parts: null, isoLocal: reason || null, display: reason || null };
}

function makeInvalidDateTime(reason = ''): ParsedDateTime {
  return {
    valid: false,
    parts: null,
    isoLocal: reason || null,
    isoLocalDateOnly: null,
    display: reason || null,
    displayWithTime: reason || null,
    hasExplicitTime: false,
  };
}

function makeValidDate(year: number, month: number, day: number): ParsedDate {
  return {
    valid: true,
    parts: { year, month, day },
    isoLocal: toIsoLocalDateOnly(year, month, day),
    display: formatDateDisplay(month, day, year),
  };
}

function makeValidDateTime(
  year: number,
  month: number,
  day: number,
  hours: number,
  minutes: number,
  hasExplicitTime: boolean,
): ParsedDateTime {
  const parts: LocalDateTimeParts = { year, month, day, hours, minutes };
  return {
    valid: true,
    parts,
    isoLocal: toIsoLocal(year, month, day, hours, minutes),
    isoLocalDateOnly: toIsoLocalDateOnly(year, month, day),
    display: formatDateDisplay(month, day, year),
    displayWithTime: hasExplicitTime
      ? `${formatDateDisplay(month, day, year)} ${padNum(hours, 2)}:${padNum(minutes, 2)}`
      : formatDateDisplay(month, day, year),
    hasExplicitTime,
  };
}

// ===========================================================================
// DATE VALIDATION AND SANITIZATION
// ===========================================================================

/**
 * Validate whether a given year/month/day combination represents a real
 * local-calendar date. Handles leap years, month lengths, etc.
 */
export function isLocalDateValid(year: number, month: number, day: number): boolean {
  if (year < 1 || month < 1 || month > 12 || day < 1) return false;

  // Use local construction then compare components back — catches overflow
  // months/days (e.g., Feb 30 -> Mar 2).
  const d = new Date(year, month - 1, day);
  return (
    d.getFullYear() === year &&
		d.getMonth() === month - 1 &&
		d.getDate() === day
  );
}

/**
 * Build a local Date object from year/month/day using the local timezone.
 * Set time to 00:00:00.000 (midnight) in the local zone.
 *
 * Contrast: Date.UTC(y, m-1, d) creates a UTC timestamp, which shifts
 * the wall clock backward by the offset when displayed locally. We want
 * the opposite -- keep the wall date intact.
 */
export function makeLocalMidnight(year: number, month: number, day: number): Date {
  return new Date(year, month - 1, day, 0, 0, 0, 0);
}

/**
 * Strip non-date characters from an input string for progressive parsing.
 * Only keeps digits and forward slashes.
 */
export function sanitizeDateString(input: string): string {
  return input.replace(/[^\d/]/g, '');
}

// ===========================================================================
// DATE-ONLY PARSING — MM/DD/YYYY → local date
// ===========================================================================

/**
 * Parse a US-format date string ("MM/DD/YYYY") into structured parts.
 * Returns an object indicating validity plus formatted outputs.
 *
 * Date-only values are completed to **local** midnight — NOT UTC.
 * This means: new Date(year, month - 1, day) uses the local timezone's
 * calendar date directly.
 */
export function parseLocalDate(input: string): ParsedDate {
  if (!input || typeof input !== 'string') return makeInvalidDate();

  const trimmed = input.trim();
  if (trimmed === '') return makeInvalidDate();

  // Accept MM/DD/YYYY or M/D/YYYY patterns
  const match = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return makeInvalidDate(trimmed);

  const [, rawMonthStr, rawDayStr, rawYearStr] = match;
  const month = Number(rawMonthStr);
  const day = Number(rawDayStr);
  const year = Number(rawYearStr);

  if (!isLocalDateValid(year, month, day)) return makeInvalidDate(trimmed);

  return makeValidDate(year, month, day);
}

// ===========================================================================
// DATE-TIME PARSING — MM/DD/YYYY HH:mm
// ===========================================================================

/**
 * Parse a datetime string in either format:
 * - US format: "MM/DD/YYYY HH:mm" or "MM/DD/YYYY"
 * - ISO format: "YYYY-MM-DDTHH:mm:ss" or "YYYY-MM-DD"
 * Time component is optional -- if omitted, defaults to 00:00 local.
 */
export function parseLocalDateTime(input: string): ParsedDateTime {
  if (!input || typeof input !== 'string') return makeInvalidDateTime();

  const trimmed = input.trim();
  if (trimmed === '') return makeInvalidDateTime();

  let year = 0; let month = 0; let day = 0; let hours = 0; let
    minutes = 0;
  let hasExplicitTime = false;

  // Try ISO datetime first: YYYY-MM-DDTHH:mm:ss
  const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (isoMatch) {
    hasExplicitTime = true;
    const [, yearStr, monthStr, dayStr, hoursStr, minutesStr] = isoMatch;
    year = Number(yearStr);
    month = Number(monthStr);
    day = Number(dayStr);
    hours = Number(hoursStr);
    minutes = Number(minutesStr);

    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
      return makeInvalidDateTime(trimmed);
    }
  } else if (trimmed.match(/^\d{4}-\d{2}-\d{2}$/)) {
    // ISO date-only: YYYY-MM-DD
    const isoDateMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoDateMatch) {
      const [, yearStr, monthStr, dayStr] = isoDateMatch;
      year = Number(yearStr);
      month = Number(monthStr);
      day = Number(dayStr);
    } else {
      return makeInvalidDateTime(trimmed);
    }
  } else {
    // Try US datetime: MM/DD/YYYY HH:mm
    const dtMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})$/);

    if (dtMatch) {
      hasExplicitTime = true;
      const [, rawMonthStr, rawDayStr, rawYearStr, rawHoursStr, rawMinsStr] = dtMatch;
      month = Number(rawMonthStr);
      day = Number(rawDayStr);
      year = Number(rawYearStr);
      hours = Number(rawHoursStr);
      minutes = Number(rawMinsStr);

      if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
        return makeInvalidDateTime(trimmed);
      }
    } else {
      // Fall back to US date-only: MM/DD/YYYY
      const result = parseLocalDate(trimmed);
      if (!result.valid) return makeInvalidDateTime(trimmed);
      ({ year, month, day } = result.parts!);
    }
  }

  if (!isLocalDateValid(year, month, day)) return makeInvalidDateTime(trimmed);

  return makeValidDateTime(year, month, day, hours, minutes, hasExplicitTime);
}

// ===========================================================================
// DISPLAY FORMATTING
// ===========================================================================

/**
 * Format a parsed date part set as MM/DD/YYYY. Returns '' on invalid.
 */
export function formatDisplay(parts: LocalDateParts | null): string {
  if (!parts || !isLocalDateValid(parts.year, parts.month, parts.day)) return '';
  return formatDateDisplay(parts.month, parts.day, parts.year);
}

// ===========================================================================
// DATE INPUT HELPERS
// ===========================================================================

/**
 * Auto-complete partial date input while typing. Given "1/5" returns "01/05/",
 * and once complete "01/05/2026" returns itself without adding slash.
 *
 * Behavior:
 *   ""           -> ""
 *   "1"          -> "01/"
 *   "1/5"        -> "01/05/"
 *   "1/5/2026"   -> "01/05/2026"
 *   "1/5/202"    -> "01/05/202"
 */
export function autoCompleteDateFormat(input: string): string {
  if (!input) return '';

  const cleaned = sanitizeDateString(input);

  const slashes = [...cleaned].filter((c) => c === '/').length;
  if (slashes >= 2) return cleaned; // already complete

  const segments = cleaned.split('/');

  // Zero-pad month and day fields
  const p0 = segments[0]?.padStart(2, '0') || '';
  const p1 = segments[1]?.padStart(2, '0') || '';
  const p2 = segments[2] || '';

  if (p2) return `${p0}/${p1}/${p2}`;
  if (p1) return `${p0}/${p1}/`;
  return `${p0}/`;
}

// ===========================================================================
// VALIDATION HELPERS (for react-hook-form resolvers later)
// ===========================================================================

export interface ValidationError {
	field: string;
	message: string;
}

/**
 * Validate a date string used in ledger entry forms.
 * Returns null when valid, or an error object when invalid.
 */
export function validateEntryDate(value: string | undefined | null): ValidationError | null {
  if (!value || value.trim() === '') {
    return { field: 'entry_date', message: 'Entry date is required' };
  }

  const result = parseLocalDate(value.trim());
  if (!result.valid) {
    return { field: 'entry_date', message: `"${value}" is not a valid date. Use MM/DD/YYYY.` };
  }

  return null;
}

/**
 * Validate a datetime string (with optional time component).
 */
export function validateEntryDateTime(value: string | undefined | null): ValidationError | null {
  if (!value || value.trim() === '') {
    return { field: 'entry_datetime', message: 'Date/time is required' };
  }

  const result = parseLocalDateTime(value.trim());
  if (!result.valid) {
    return { field: 'entry_datetime', message: `"${value}" is not a valid date` };
  }

  return null;
}
